/**
 * VaultListController — page /coffre.html (EPIC-035 / 35.4, spec §24.E).
 *
 * Liste paginée des documents du coffre-fort avec filtres synchronisés en
 * querystring, indicateur de quota (uniquement sur les imports manuels),
 * empty states et actions par ligne (Télécharger / Voir / Modifier / Archiver).
 *
 * Endpoints consommés (cf. api.js EPIC-035/35.4) :
 *   - GET  /v1/vault                  → listing + quota
 *   - POST /v1/vault/{id}/archive     → soft-delete manuel
 *   - GET  /v1/vault/{id}/download    → redirect 302 (URL signée 15 min)
 *
 * Conventions :
 *   - 0 HTML inline (templates + cloneTemplate + fillSlots).
 *   - Aucune classe inventée — toutes ⊆ notamicro.css.
 *   - Vocabulaire UI : « Plateforme Agréée (PA) », pas « PDP », pas NotaFacto.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const QUOTA_WARNING_THRESHOLD = 80;
const QUOTA_FULL_THRESHOLD = 100;

const DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const SIZE_UNITS = ["o", "Ko", "Mo", "Go"];

export default class VaultListController extends BaseController {
  static targets = [
    "quotaSection", "quotaBar", "quotaFill", "quotaHint", "quotaAlert",
    "importLink", "headerImportLink",
    "filtersForm", "filterType", "filterCategory", "filterFiscalYear",
    "filterDateFrom", "filterDateTo", "filterSearch", "filterIncludeArchived",
    "tableWrap", "tableBody",
    "loading", "error", "empty", "emptyFiltered", "historyNotice",
    "pagination", "paginationSummary", "paginationPages",
  ];

  _searchDebounceId = null;
  _currentPage = 1;
  _totalPages = 1;
  _totalCount = 0;

  connect() {
    this._restoreFiltersFromQuerystring();
    this._loadDocuments();
  }

  disconnect() {
    if (this._searchDebounceId) {
      clearTimeout(this._searchDebounceId);
      this._searchDebounceId = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Loading + rendering
  // ---------------------------------------------------------------------------

  async _loadDocuments() {
    this._showLoading();

    try {
      const params = this._buildRequestParams();
      const resp = await api.vault.list(params);

      const documents = Array.isArray(resp?.data) ? resp.data : [];
      const pagination = resp?.pagination || {};
      const meta = resp?.meta || {};

      this._currentPage = Number(pagination.page) || 1;
      this._totalPages = Number(pagination.totalPages) || 1;
      this._totalCount = Number(pagination.total) || documents.length;

      this._renderQuota(meta.quota || null);
      this._renderHistoryNotice(meta.autoCount);
      this._renderRows(documents);
      this._renderPagination(documents.length);
    } catch (err) {
      this._showError(this._extractMessage(err) || "Impossible de charger votre coffre-fort.");
    }
  }

  _renderRows(documents) {
    const tbody = this.tableBodyTarget;
    tbody.textContent = "";

    if (documents.length === 0) {
      if (this._hasActiveFilters()) {
        this._showEmptyFiltered();
      } else {
        this._showEmpty();
      }
      return;
    }

    documents.forEach((doc) => tbody.appendChild(this._buildRow(doc)));
    this._showTable();
  }

  _buildRow(doc) {
    const fragment = this.cloneTemplate("vault-row-template");
    const tr = fragment.querySelector("tr");
    if (!tr) return fragment;

    tr.dataset.id = doc.id;
    if (doc.status === "archived") tr.dataset.archived = "true";

    const date = doc.documentDate || doc.createdAt || null;
    const dateEl = tr.querySelector('[data-slot="dateLabel"]');
    if (dateEl) {
      if (date) {
        dateEl.setAttribute("datetime", date);
        dateEl.textContent = this._formatDate(date);
      } else {
        dateEl.textContent = "—";
      }
    }

    const titleLink = tr.querySelector('[data-slot="titleLink"]');
    if (titleLink) {
      titleLink.href = `/coffre-detail.html?id=${encodeURIComponent(doc.id)}`;
      titleLink.textContent = doc.title || "—";
    }

    this.fillSlots(tr, {
      categoryLabel: this._formatCategory(doc.category),
      fiscalYear: doc.fiscalYear ?? "—",
      size: this._formatSize(doc.fileSizeBytes),
    });

    const typeBadgeCell = tr.querySelector('[data-slot="typeBadge"]');
    if (typeBadgeCell) {
      const pillId = this._isAutoSource(doc.source)
        ? "vault-row-pill-auto-template"
        : "vault-row-pill-manual-template";
      typeBadgeCell.appendChild(this.cloneTemplate(pillId));
    }

    const actionsCell = tr.querySelector('[data-slot="actions"]');
    if (actionsCell) this._fillActionsCell(actionsCell, doc);

    return tr;
  }

  _fillActionsCell(cell, doc) {
    const title = doc.title || "ce document";

    cell.appendChild(this._buildActionLink({
      href: api.vault.downloadUrl(doc.id),
      label: "Télécharger",
      ariaLabel: `Télécharger ${title}`,
    }));

    cell.appendChild(this._buildActionLink({
      href: `/coffre-detail.html?id=${encodeURIComponent(doc.id)}`,
      label: "Voir",
      ariaLabel: `Voir les détails de ${title}`,
    }));

    const isManualActive = !this._isAutoSource(doc.source) && doc.status !== "archived";
    if (isManualActive) {
      cell.appendChild(this._buildActionLink({
        href: `/coffre-detail.html?id=${encodeURIComponent(doc.id)}#edit`,
        label: "Modifier",
        ariaLabel: `Modifier les métadonnées de ${title}`,
      }));

      const archiveBtn = this._buildActionButton({
        label: "Archiver",
        ariaLabel: `Archiver ${title}`,
      });
      archiveBtn.dataset.id = doc.id;
      archiveBtn.dataset.title = title;
      archiveBtn.dataset.action = "click->vault-list#handleArchive";
      cell.appendChild(archiveBtn);
    }
  }

  _buildActionLink({ href, label, ariaLabel }) {
    const fragment = this.cloneTemplate("vault-row-action-link-template");
    const a = fragment.querySelector("a");
    if (!a) return fragment;
    a.href = href;
    a.setAttribute("aria-label", ariaLabel);
    a.textContent = label;
    return a;
  }

  _buildActionButton({ label, ariaLabel }) {
    const fragment = this.cloneTemplate("vault-row-action-button-template");
    const btn = fragment.querySelector("button");
    if (!btn) return fragment;
    btn.setAttribute("aria-label", ariaLabel);
    btn.textContent = label;
    return btn;
  }

  // ---------------------------------------------------------------------------
  // Quota indicator (§24.D — 5 Go sur manual_upload uniquement)
  // ---------------------------------------------------------------------------

  _renderQuota(quota) {
    if (!quota || typeof quota.usedBytes !== "number" || typeof quota.quotaBytes !== "number") {
      return;
    }

    const usedBytes = Math.max(0, Math.round(quota.usedBytes));
    const quotaBytes = Math.max(1, Math.round(quota.quotaBytes));
    const usedPercent = Math.min(
      100,
      Math.max(0, Math.round(quota.usedPercent ?? (usedBytes / quotaBytes) * 100)),
    );

    if (this.hasQuotaFillTarget) {
      this.quotaFillTarget.style.width = `${usedPercent}%`;
    }
    if (this.hasQuotaBarTarget) {
      this.quotaBarTarget.setAttribute("aria-valuenow", String(usedPercent));
    }
    if (this.hasQuotaHintTarget) {
      this.quotaHintTarget.textContent =
        `${this._formatSize(usedBytes)} / ${this._formatSize(quotaBytes)} utilisés · ${usedPercent} % · les documents auto ne comptent pas dans ce quota`;
    }

    const isFull = usedPercent >= QUOTA_FULL_THRESHOLD;
    const isWarning = usedPercent >= QUOTA_WARNING_THRESHOLD;

    if (this.hasQuotaAlertTarget) {
      if (isFull) {
        this.quotaAlertTarget.querySelector("p").textContent =
          "Quota atteint. Archivez d'anciens documents pour libérer de l'espace.";
        this.quotaAlertTarget.hidden = false;
      } else if (isWarning) {
        this.quotaAlertTarget.querySelector("p").textContent =
          "Vous approchez du quota d'archivage manuel (80 %). Pensez à archiver les documents non essentiels.";
        this.quotaAlertTarget.hidden = false;
      } else {
        this.quotaAlertTarget.hidden = true;
      }
    }

    this._setImportButtonsDisabled(isFull);
  }

  _setImportButtonsDisabled(disabled) {
    const links = [];
    if (this.hasHeaderImportLinkTarget) links.push(this.headerImportLinkTarget);
    if (this.hasImportLinkTarget) links.push(this.importLinkTarget);

    links.forEach((link) => {
      if (disabled) {
        link.setAttribute("aria-disabled", "true");
        link.setAttribute("tabindex", "-1");
        link.dataset.disabled = "true";
        link.title = "Quota atteint. Archivez d'anciens documents pour libérer de l'espace.";
      } else {
        link.removeAttribute("aria-disabled");
        link.removeAttribute("tabindex");
        delete link.dataset.disabled;
        link.removeAttribute("title");
      }
    });
  }

  _renderHistoryNotice(autoCount) {
    if (!this.hasHistoryNoticeTarget) return;
    const count = Number(autoCount);
    const show = Number.isFinite(count) && count === 0;
    this.historyNoticeTarget.hidden = !show;
  }

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------

  _renderPagination(pageCount) {
    if (!this.hasPaginationTarget) return;

    const hasResults = pageCount > 0;
    this.paginationTarget.hidden = !hasResults;

    if (!hasResults) return;

    const firstIndex = (this._currentPage - 1) * PER_PAGE + 1;
    const lastIndex = Math.min(firstIndex + pageCount - 1, this._totalCount);

    if (this.hasPaginationSummaryTarget) {
      this.paginationSummaryTarget.textContent =
        `${firstIndex} — ${lastIndex} sur ${this._totalCount} document${this._totalCount > 1 ? "s" : ""}`;
    }

    if (!this.hasPaginationPagesTarget) return;
    const container = this.paginationPagesTarget;
    container.textContent = "";

    const prev = this._buildPaginationButton({
      label: "‹",
      ariaLabel: "Page précédente",
      page: this._currentPage - 1,
      disabled: this._currentPage <= 1,
    });
    container.appendChild(prev);

    const pages = this.getPagesToShow(this._currentPage, this._totalPages);
    pages.forEach((p) => {
      if (p === "...") {
        const span = document.createElement("span");
        span.textContent = "…";
        span.setAttribute("aria-hidden", "true");
        container.appendChild(span);
        return;
      }
      const btn = this._buildPaginationButton({
        label: String(p),
        ariaLabel: `Page ${p}`,
        page: p,
        current: p === this._currentPage,
      });
      container.appendChild(btn);
    });

    const next = this._buildPaginationButton({
      label: "›",
      ariaLabel: "Page suivante",
      page: this._currentPage + 1,
      disabled: this._currentPage >= this._totalPages,
    });
    container.appendChild(next);
  }

  _buildPaginationButton({ label, ariaLabel, page, current = false, disabled = false }) {
    const fragment = this.cloneTemplate("vault-pagination-page-template");
    const btn = fragment.querySelector("button");
    if (!btn) return fragment;
    btn.textContent = label;
    btn.setAttribute("aria-label", ariaLabel);
    if (current) {
      btn.classList.add("is-current");
      btn.setAttribute("aria-current", "page");
    }
    if (disabled) {
      btn.disabled = true;
    } else {
      btn.dataset.page = String(page);
      btn.dataset.action = "click->vault-list#handlePageChange";
    }
    return btn;
  }

  handlePageChange(event) {
    const page = Number(event.currentTarget?.dataset?.page);
    if (!Number.isFinite(page) || page < 1 || page > this._totalPages) return;
    this._currentPage = page;
    this._syncQuerystring();
    this._loadDocuments();
  }

  // ---------------------------------------------------------------------------
  // Filtres
  // ---------------------------------------------------------------------------

  handleFilterChange(event) {
    const target = event?.target;
    if (target === this.filterSearchTarget) {
      if (this._searchDebounceId) clearTimeout(this._searchDebounceId);
      this._searchDebounceId = setTimeout(() => {
        this._searchDebounceId = null;
        this._currentPage = 1;
        this._syncQuerystring();
        this._loadDocuments();
      }, SEARCH_DEBOUNCE_MS);
      return;
    }

    this._currentPage = 1;
    this._syncQuerystring();
    this._loadDocuments();
  }

  handleReset(event) {
    if (event) event.preventDefault();

    if (this.hasFiltersFormTarget) this.filtersFormTarget.reset();

    if (this.hasFilterTypeTarget) this.filterTypeTarget.value = "tous";
    if (this.hasFilterCategoryTarget) this.filterCategoryTarget.value = "tous";
    if (this.hasFilterFiscalYearTarget) this.filterFiscalYearTarget.value = "tous";
    if (this.hasFilterDateFromTarget) this.filterDateFromTarget.value = "";
    if (this.hasFilterDateToTarget) this.filterDateToTarget.value = "";
    if (this.hasFilterSearchTarget) this.filterSearchTarget.value = "";
    if (this.hasFilterIncludeArchivedTarget) this.filterIncludeArchivedTarget.checked = false;

    this._currentPage = 1;
    this._syncQuerystring();
    this._loadDocuments();
  }

  // ---------------------------------------------------------------------------
  // Archivage (§24.F.2)
  // ---------------------------------------------------------------------------

  async handleArchive(event) {
    const btn = event?.currentTarget;
    if (!btn) return;
    const id = btn.dataset.id;
    const title = btn.dataset.title || "ce document";
    if (!id) return;

    const ok = await this.confirm(
      `Archiver « ${title} » ? Si ce document est requis légalement, vous devez conserver une copie hors Notamicro.`,
      { title: "Archiver le document", confirmLabel: "Archiver" },
    );
    if (!ok) return;

    try {
      await api.vault.archive(id);
      this.showSuccess("Document archivé.");
      this._loadDocuments();
    } catch (err) {
      this.showError(this._extractMessage(err) || "L'archivage a échoué.");
    }
  }

  // ---------------------------------------------------------------------------
  // Querystring sync
  // ---------------------------------------------------------------------------

  _restoreFiltersFromQuerystring() {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    const setIfPresent = (param, target) => {
      if (!target) return;
      const value = params.get(param);
      if (value !== null) target.value = value;
    };

    if (this.hasFilterTypeTarget) setIfPresent("type", this.filterTypeTarget);
    if (this.hasFilterCategoryTarget) setIfPresent("category", this.filterCategoryTarget);
    if (this.hasFilterFiscalYearTarget) setIfPresent("fiscalYear", this.filterFiscalYearTarget);
    if (this.hasFilterDateFromTarget) setIfPresent("dateFrom", this.filterDateFromTarget);
    if (this.hasFilterDateToTarget) setIfPresent("dateTo", this.filterDateToTarget);
    if (this.hasFilterSearchTarget) setIfPresent("search", this.filterSearchTarget);
    if (this.hasFilterIncludeArchivedTarget) {
      this.filterIncludeArchivedTarget.checked = params.get("includeArchived") === "1";
    }

    const page = Number(params.get("page"));
    if (Number.isFinite(page) && page >= 1) this._currentPage = page;
  }

  _syncQuerystring() {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    const setOrDelete = (key, value, defaultValue = "") => {
      if (value === defaultValue || value === null || value === undefined) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    };

    setOrDelete("type", this._getValue(this.filterTypeTarget), "tous");
    setOrDelete("category", this._getValue(this.filterCategoryTarget), "tous");
    setOrDelete("fiscalYear", this._getValue(this.filterFiscalYearTarget), "tous");
    setOrDelete("dateFrom", this._getValue(this.filterDateFromTarget));
    setOrDelete("dateTo", this._getValue(this.filterDateToTarget));
    setOrDelete("search", this._getValue(this.filterSearchTarget));

    if (this.hasFilterIncludeArchivedTarget && this.filterIncludeArchivedTarget.checked) {
      params.set("includeArchived", "1");
    } else {
      params.delete("includeArchived");
    }

    if (this._currentPage > 1) {
      params.set("page", String(this._currentPage));
    } else {
      params.delete("page");
    }

    window.history.replaceState({}, "", url.toString());
  }

  _buildRequestParams() {
    const params = { page: this._currentPage, perPage: PER_PAGE, sort: "-documentDate" };

    const type = this._getValue(this.filterTypeTarget);
    if (type && type !== "tous") params.type = type;

    const category = this._getValue(this.filterCategoryTarget);
    if (category && category !== "tous") params.category = category;

    const fiscalYear = this._getValue(this.filterFiscalYearTarget);
    if (fiscalYear && fiscalYear !== "tous") params.fiscalYear = fiscalYear;

    const dateFrom = this._getValue(this.filterDateFromTarget);
    if (dateFrom) params.dateFrom = dateFrom;

    const dateTo = this._getValue(this.filterDateToTarget);
    if (dateTo) params.dateTo = dateTo;

    const search = this._getValue(this.filterSearchTarget);
    if (search) params.search = search;

    if (this.hasFilterIncludeArchivedTarget && this.filterIncludeArchivedTarget.checked) {
      params.includeArchived = "1";
    }

    return params;
  }

  _hasActiveFilters() {
    if (this._getValue(this.filterTypeTarget) && this._getValue(this.filterTypeTarget) !== "tous") return true;
    if (this._getValue(this.filterCategoryTarget) && this._getValue(this.filterCategoryTarget) !== "tous") return true;
    if (this._getValue(this.filterFiscalYearTarget) && this._getValue(this.filterFiscalYearTarget) !== "tous") return true;
    if (this._getValue(this.filterDateFromTarget)) return true;
    if (this._getValue(this.filterDateToTarget)) return true;
    if (this._getValue(this.filterSearchTarget)) return true;
    if (this.hasFilterIncludeArchivedTarget && this.filterIncludeArchivedTarget.checked) return true;
    return false;
  }

  // ---------------------------------------------------------------------------
  // States
  // ---------------------------------------------------------------------------

  _showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = false;
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true;
    if (this.hasEmptyFilteredTarget) this.emptyFilteredTarget.hidden = true;
    if (this.hasTableWrapTarget) this.tableWrapTarget.hidden = true;
    if (this.hasPaginationTarget) this.paginationTarget.hidden = true;
  }

  _showTable() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true;
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true;
    if (this.hasEmptyFilteredTarget) this.emptyFilteredTarget.hidden = true;
    if (this.hasTableWrapTarget) this.tableWrapTarget.hidden = false;
  }

  _showEmpty() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true;
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
    if (this.hasTableWrapTarget) this.tableWrapTarget.hidden = true;
    if (this.hasEmptyTarget) this.emptyTarget.hidden = false;
    if (this.hasEmptyFilteredTarget) this.emptyFilteredTarget.hidden = true;
    if (this.hasPaginationTarget) this.paginationTarget.hidden = true;
  }

  _showEmptyFiltered() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true;
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
    if (this.hasTableWrapTarget) this.tableWrapTarget.hidden = true;
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true;
    if (this.hasEmptyFilteredTarget) this.emptyFilteredTarget.hidden = false;
    if (this.hasPaginationTarget) this.paginationTarget.hidden = true;
  }

  _showError(message) {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true;
    if (this.hasTableWrapTarget) this.tableWrapTarget.hidden = true;
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true;
    if (this.hasEmptyFilteredTarget) this.emptyFilteredTarget.hidden = true;
    if (this.hasPaginationTarget) this.paginationTarget.hidden = true;
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.hidden = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _getValue(target) {
    if (!target) return "";
    return (target.value || "").trim();
  }

  _isAutoSource(source) {
    return typeof source === "string" && source.startsWith("auto_");
  }

  _formatDate(value) {
    if (!value) return "—";
    try {
      return DATE_FORMATTER.format(new Date(value));
    } catch {
      return value;
    }
  }

  _formatSize(bytes) {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n < 0) return "—";
    if (n === 0) return "0 o";
    let value = n;
    let unit = 0;
    while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
      value /= 1024;
      unit += 1;
    }
    const formatted = value >= 100 || unit === 0
      ? Math.round(value).toString()
      : value.toFixed(value >= 10 ? 0 : 1).replace(".", ",");
    return `${formatted} ${SIZE_UNITS[unit]}`;
  }

  _formatCategory(value) {
    if (!value) return "—";
    if (typeof value !== "string") return String(value);
    return value
      .split(/[_\s]+/)
      .map((word, idx) => (idx === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
      .join(" ");
  }

  _extractMessage(err) {
    if (err instanceof APIError) {
      const data = err.data || {};
      if (typeof data.message === "string" && data.message) return data.message;
      if (typeof data.error === "string" && data.error) {
        return this._humanizeErrorCode(data.error);
      }
    }
    if (err && typeof err.message === "string") return err.message;
    return null;
  }

  _humanizeErrorCode(code) {
    switch (code) {
      case "vault_document_immutable":
        return "Ce document est immuable et ne peut pas être archivé.";
      case "vault_quota_exceeded":
        return "Quota atteint. Archivez d'anciens documents pour libérer de l'espace.";
      case "vault_file_too_large":
        return "Fichier trop volumineux.";
      case "vault_invalid_mime":
        return "Format de fichier non supporté.";
      default:
        return "Une erreur est survenue.";
    }
  }
}
